// client/src/components/auth/ConnectionProblem.tsx (ST-00303)
//
// "Can't reach LexiBridge": shown on the sign-in route and on protected routes
// while the session check gets no answer from the server (no network, a company
// web filter, a deploy). It replaces the sign-in form, which would only ask for a
// password that cannot be checked — and which looked like being signed out. The
// check repeats by itself (AuthContext); "Try again" runs it at once.
import { useEffect, useRef, useState } from 'react';
import { RefreshCw, WifiOff } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import {
  CONNECTION_PROBLEM_MESSAGE,
  CONNECTION_PROBLEM_RETRY_NOTE,
  CONNECTION_PROBLEM_TITLE,
} from './connectionProblemText';
import './Login.css';

const ConnectionProblem = () => {
  const { retryConnection } = useAuth();
  const [trying, setTrying] = useState(false);
  // A "Try again" that still got no answer (the card is still up afterwards).
  const [stillDown, setStillDown] = useState(false);
  // A successful retry unmounts the card: no state updates after that.
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const handleRetry = async () => {
    if (trying) return;
    setTrying(true);
    setStillDown(false);
    try {
      await retryConnection();
    } finally {
      if (mounted.current) {
        setTrying(false);
        setStillDown(true);
      }
    }
  };

  return (
    <div className="login-page">
      <div className="login-bg-grid" />
      <div className="login-bg-glow login-bg-glow--teal" />
      <div className="login-bg-glow login-bg-glow--purple" />

      <section className="login-card connection-card" aria-labelledby="connection-problem-title">
        <div className="connection-wordmark login-wordmark" role="img" aria-label="LexiBridge">
          <span className="lw-lexi">Lexi</span>
          <span className="lw-divider" />
          <span className="lw-bridge">Bridge</span>
        </div>

        <div className="connection-icon" aria-hidden="true">
          <WifiOff size={24} />
        </div>

        <h1 id="connection-problem-title" className="connection-title">
          {CONNECTION_PROBLEM_TITLE}
        </h1>
        <p className="connection-text">{CONNECTION_PROBLEM_MESSAGE}</p>

        <button type="button" className="login-button" onClick={handleRetry} disabled={trying}>
          <RefreshCw
            size={18}
            className={trying ? 'connection-spin' : undefined}
            aria-hidden="true"
          />
          {trying ? 'Trying…' : 'Try again'}
        </button>

        <p className="connection-status" role="status">
          {stillDown && !trying ? 'Still no answer.' : ''}
        </p>
        <p className="connection-note">{CONNECTION_PROBLEM_RETRY_NOTE}</p>
      </section>
    </div>
  );
};

export default ConnectionProblem;
